import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabaseClient';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { DashboardSkeleton } from '../../components/ui/LoadingState';
import AssetQuickViewPanel from './components/AssetQuickViewPanel';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import { formatAssetStatus } from '../../utils/formatters';
import { exportToProfessionalCSV } from '../../utils/csvExport';
import { useSelector } from 'react-redux';
import { useData } from '../../context/DataContext';
import { usePreferences } from '../../hooks/usePreferences';
import { logActivity } from '../../utils/activityLogger';
import { FilterToolbar } from './components/FilterToolbar';
import AssetTable from './components/AssetTable';
import MfaChallengeModal from '../../components/ui/MfaChallengeModal';
import QRCodeModal from '../asset-details/components/QRCodeModal';

const AssetList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const urlSearchQuery = searchParams.get('search') || '';

    const { currentUser, isLoading: isContextLoading } = useData();
    const { preferences } = usePreferences();
    const isPic = currentUser?.role === 'pic' || currentUser?.role === 'department_pic';
    const canDeleteAsset = currentUser?.role === 'admin' || currentUser?.role === 'system_admin' || currentUser?.role === 'department_pic';
    const [assets, setAssets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [assetToDelete, setAssetToDelete] = useState(null);
    const [showMfaModal, setShowMfaModal] = useState(false);
    const [enrolledFactors, setEnrolledFactors] = useState([]);
    const [qrAsset, setQrAsset] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const [notifications, setNotifications] = useState([]);

    const userId = currentUser?.id;

    // Filter States
    const [filters, setFilters] = useState({
        searchQuery: urlSearchQuery,
        category: '',
        status: [],
        department: isPic ? currentUser?.department_id : '',
        supplier: '',
        location: '',
        dateRange: { start: '', end: '' }
    });

    // Data for filters
    const [departments, setDepartments] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [locations, setLocations] = useState([]);

    const addNotification = (message, type) => {
        setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
    };

    const fetchAssets = async () => {
        if (!currentUser) return;
        setIsLoading(true);
        try {
            let query = supabase
                .from('assets')
                .select(`
                    *, 
                    departments ( name ), 
                    suppliers ( company_name ),
                    locations ( name ),
                    maintenance ( cost ),
                    loans (
                        status,
                        employees ( id, full_name, email, department_id, departments ( name ) ),
                        departments ( id, name )
                    )
                `);

            // Role-Based and Department Filtering
            if (isPic && currentUser?.department_id) {
                // Fetch all and filter client-side to include employee-assigned assets
            } else if (filters.department) {
                query = query.eq('current_department_id', filters.department);
            }

            // Location Filter (Server-Side)
            if (filters.location) {
                query = query.eq('location_id', filters.location);
            }

            // Status Filter (Server-Side)
            if (filters.status && filters.status.length > 0) {
                query = query.in('status', filters.status);
            }

            // Supplier Filter (Server-Side)
            if (filters.supplier) {
                query = query.eq('supplier_id', filters.supplier);
            }

            // Sorting (Server-Side for main columns)
            if (sortConfig.key && !sortConfig.key.includes('.')) {
                query = query.order(sortConfig.key, { ascending: sortConfig.direction === 'asc' });
            }

            const { data, error } = await query;
            if (error) throw error;

            let filtered = data || [];
            if (isPic && currentUser?.department_id) {
                filtered = filtered.filter(asset => 
                    asset.current_department_id === currentUser.department_id ||
                    asset.loans?.some(loan => 
                        (loan.status === 'active' || loan.status === 'In Use' || loan.status === 'overdue') && 
                        loan.employees?.department_id === currentUser.department_id
                    )
                );
            }

            setAssets(filtered);
        } catch (error) {
            console.error('Error fetching assets:', error);
            addNotification(t('asset_list.fetch_assets_error', { message: error.message }), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (currentUser) {
            fetchAssets();
        }
    }, [currentUser, filters.location, filters.category, filters.status, filters.department, filters.supplier, sortConfig.key, sortConfig.direction]);

    const handleAssetClick = (asset) => setSelectedAsset(asset);
    const closePanel = () => setSelectedAsset(null);
    const handleOpenQrModal = (asset) => setQrAsset(asset);
    const handleCloseQrModal = () => setQrAsset(null);

    const handleFilterChange = (newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const filteredAssets = useMemo(() => {
        let result = [...assets];

        // Apply Category Filter (Client-side for case-insensitive robustness)
        if (filters.category) {
            const queryCat = filters.category.toLowerCase();
            result = result.filter(a => a.category?.toLowerCase() === queryCat);
        }

        // Apply Search (Keep on Client for responsiveness)
        if (filters.searchQuery) {
            const query = filters.searchQuery.toLowerCase();
            result = result.filter(a => 
                a.product_name?.toLowerCase().includes(query) ||
                a.asset_tag?.toLowerCase().includes(query) ||
                a.serial_number?.toLowerCase().includes(query)
            );
        }

        // Apply Date Range (Keep on Client as it's complex to query date ranges in some Supabase setups)
        if (filters.dateRange.start) {
            result = result.filter(a => new Date(a.purchase_date) >= new Date(filters.dateRange.start));
        }
        if (filters.dateRange.end) {
            result = result.filter(a => new Date(a.purchase_date) <= new Date(filters.dateRange.end));
        }

        // Secondary Sorting (for joined tables or complex keys)
        if (sortConfig.key.includes('.')) {
            result.sort((a, b) => {
                const [table, col] = sortConfig.key.split('.');
                const valA = a[table]?.[col];
                const valB = b[table]?.[col];
                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [assets, filters.searchQuery, filters.dateRange, sortConfig]);

    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const { data: departmentsData } = await supabase.from('departments').select('id, name');
                setDepartments(departmentsData?.map(dep => ({ value: dep.id, label: dep.name })) || []);

                const { data: suppliersData } = await supabase.from('suppliers').select('id, company_name');
                setSuppliers(suppliersData?.map(sup => ({ value: sup.id, label: sup.company_name })) || []);

                const { data: locationsData } = await supabase.from('locations').select('id, name');
                setLocations(locationsData?.map(loc => ({ value: loc.id, label: loc.name })) || []);
            } catch (error) {
                addNotification(t('asset_list.fetch_filters_error', { message: error.message }), 'error');
            }
        };
        const fetchMfaFactors = async () => {
            const { data } = await supabase.auth.mfa.listFactors();
            if (data) setEnrolledFactors(data.totp);
        }
        fetchFilterOptions();
        fetchMfaFactors();
    }, []);

    useEffect(() => {
        if (location.state?.message) {
            addNotification(location.state.message, 'success');
            window.history.replaceState({}, '');
        }
    }, [location.state]);
    
    const handleExportCSV = () => {
        if (filteredAssets.length === 0) {
            addNotification(t('asset_list.no_data_to_export'), 'warning');
            return;
        }

        const allColumns = [
            { key: 'asset_tag', label: t('labels.asset_tag') },
            { key: 'product_name', label: t('labels.product_name') },
            { key: 'category', label: t('labels.category') },
            { key: 'status', label: t('labels.status') },
            { key: 'locations', label: t('labels.location'), value: (asset) => asset.locations?.name || '' },
            { key: 'departments', label: t('labels.department'), value: (asset) => asset.departments?.name || '' },
            { key: 'suppliers', label: t('labels.supplier'), value: (asset) => asset.suppliers?.company_name || '' },
            { key: 'purchase_date', label: t('labels.purchase_date') },
            { key: 'purchase_price', label: t('labels.purchase_price') },
            { key: 'serial_number', label: t('labels.serial_number') },
        ];

        // Get user's export default columns preference
        const exportDefaultColumns = preferences?.export_default_columns || [];
        console.log('🎯 Using export preferences:', exportDefaultColumns);

        exportToProfessionalCSV({
            data: filteredAssets,
            reportTitle: isPic ? t('asset_list.my_department_assets') : t('asset_list.asset_master_inventory'),
            appliedFilters: {
                Search: filters.searchQuery || t('common.none'),
                Category: filters.category || t('asset_list.all_categories'),
                Status: filters.status || t('asset_list.all_statuses'),
                Location: filters.location || t('asset_list.all_locations'),
                Department: filters.department || t('asset_list.all_departments'),
                Supplier: filters.supplier || t('asset_list.all_suppliers'),
                PurchaseDate: filters.dateRange.start || filters.dateRange.end ? `${filters.dateRange.start || t('asset_list.any_date')} ${t('common.to')} ${filters.dateRange.end || t('asset_list.any_date')}` : t('asset_list.any_date'),
            },
            fileNamePrefix: 'panasonic_assets_export',
            columns: allColumns,
            exportDefaultColumns: exportDefaultColumns,
        });
        
        addNotification(t('asset_list.export_success'), 'success');
    };

    const handleDeleteAsset = (asset) => {
        if (enrolledFactors.length > 0) {
            setAssetToDelete(asset);
            setShowMfaModal(true);
        } else {
            if (window.confirm(t('asset_list.delete_confirm', { name: asset.product_name, tag: asset.asset_tag }))) {
                executeDelete(asset);
            }
        }
    };

    const executeDelete = async (asset) => {
        try {
            await logActivity('asset_deleted', t('asset_list.deleted_asset_log', { name: asset.product_name }), asset.asset_tag, userId);
            const { error } = await supabase.from('assets').delete().eq('asset_tag', asset.asset_tag);
            if (error) throw error;
            addNotification(t('asset_list.delete_success'), 'success');
            // DataContext will handle re-fetching if query is invalidated
        } catch (error) {
            addNotification(t('asset_list.delete_error', { message: error.message }), 'error');
        }
        setShowMfaModal(false);
        setAssetToDelete(null);
    };

    return (
        <div>
            <NotificationContainer notifications={notifications} onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} />
            <div className="space-y-6">
                
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">{isPic ? t('asset_list.my_department_assets') : t('asset_list.page_title')}</h1>
                        <p className="text-muted-foreground">{isPic ? t('asset_list.my_department_assets_subtitle') : t('asset_list.page_subtitle')}</p>
                    </div>
                    {!isPic && (
                        <Button iconName="Plus" onClick={() => navigate('/asset-registration')}>{t('asset_list.add_new_asset')}</Button>
                    )}
                </div>

                <FilterToolbar
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    totalCount={filteredAssets.length}
                    departments={departments}
                    suppliers={suppliers}
                    locations={locations}
                    onExport={handleExportCSV}
                    hideDepartmentFilter={isPic}
                />

                <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
                    {isLoading ? (
                        <div className="p-6"><DashboardSkeleton /></div>
                    ) : filteredAssets.length === 0 ? (
                        <div className="p-12 text-center text-muted-foreground">
                            <Icon name="Package" size={48} className="mx-auto mb-4 opacity-20" />
                            <p>{t('asset_list.no_assets_found')}</p>
                        </div>
                    ) : (
                        <AssetTable
                            assets={filteredAssets}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            onAssetClick={handleAssetClick}
                            onOpenQrModal={handleOpenQrModal}
                            onDeleteAsset={handleDeleteAsset}
                            isPic={isPic}
                            canDeleteAsset={canDeleteAsset}
                        />
                    )}
                </div>
                
                <div className="text-xs text-muted-foreground text-center mt-4">
                    {t('asset_list.showing_total_assets', { count: filteredAssets.length })}
                </div>
            </div>
            
            <AnimatePresence>
                <AssetQuickViewPanel 
                    isOpen={!!selectedAsset} 
                    onClose={closePanel} 
                    asset={selectedAsset} 
                    isPic={isPic}
                />
            </AnimatePresence>

            <AnimatePresence>
                {qrAsset && (
                    <QRCodeModal asset={qrAsset} onClose={handleCloseQrModal} />
                )}
            </AnimatePresence>

            {showMfaModal && (
                <MfaChallengeModal
                    factorId={enrolledFactors[0].id}
                    onCancel={() => {
                        setShowMfaModal(false);
                        setAssetToDelete(null);
                    }}
                    onSuccess={() => executeDelete(assetToDelete)}
                />
            )}
        </div>
    );
};

export default AssetList;
